import { computed, inject, Injectable } from '@angular/core';
import { HttpHeaders, HttpResponse, HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { RsOverviewServerSideStateManager } from '@rs-shared-lib/abstract-classes/rs-overview-state/rs-overview-server-side-state-manager';
import { RsPersistOverviewState } from '@rs-shared-lib/abstract-classes/rs-overview-state/persistency/rs-persist-overview-state.decorator';
import { rsCountFilters } from '@rs-shared-lib/utils/count-filters/count-filters';
import { UserService } from '@core/services/user.service';
import { LeasingCompanyPartsReductionSummary } from '@shared/models/leasing-company-parts-reduction/leasing-company-parts-reduction-summary';
import { MRT_DEFAULT_PAGE_SIZE, MRT_PAGE_SIZE_OPTIONS } from '@shared/constants/mrt-page-size-options';
import { environment } from '@environments/environment';
import { ServerSideQueryParams } from '@rs-shared-lib/abstract-classes/rs-overview-state/models/rs-overview-server-side-state-manager';
import { RsUtils } from '@lib/rs-ngx';

export type ActivityGroupReductionFilters = {
  activityGroupCode: string;
  supplierGroupCode: string;
};

@RsPersistOverviewState({
  storageKeySuffix: 'MrtLeasingCompanyActivityGroupReductions',
  includeUserContext: () => inject(UserService).getCurrentUser()!.loginString,
})
@Injectable()
export class ActivityGroupReductionsOverviewStateService extends RsOverviewServerSideStateManager<LeasingCompanyPartsReductionSummary, ActivityGroupReductionFilters> {
  public readonly filterCount = computed(() => rsCountFilters(this.filters()));

  constructor() {
    const http = inject(HttpClient);

    super({
      serviceMethod: (params: ServerSideQueryParams<ActivityGroupReductionFilters>) => {
        const {
          _page, _pageSize, activityGroupCode, supplierGroupCode,
        } = params;
        const filters = RsUtils.trimEmptyHttpParams({
          page: _page,
          resultsPerPage: _pageSize,
          activityGroupCode: activityGroupCode ?? undefined,
          supplierGroupCode: supplierGroupCode ?? undefined,
        });

        return forkJoin([
          http.get<LeasingCompanyPartsReductionSummary[]>(`${environment.apiUrl}/leasing-company/parts-reduction`, { params: filters }),
          http.get<number>(`${environment.apiUrl}/leasing-company/parts-reduction/pagecount`, { params: filters }),
        ]).pipe(
          map(([data, pageCount]) =>
            new HttpResponse<LeasingCompanyPartsReductionSummary[]>({
              body: data,
              headers: new HttpHeaders({
                resultcount: String(pageCount * _pageSize),
                pagecount: String(pageCount),
              }),
            }),
          ),
        );
      },
      initialFiltersState: {
        activityGroupCode: '',
        supplierGroupCode: '',
      },
      initialPagination: { pageIndex: 0, pageSize: MRT_DEFAULT_PAGE_SIZE },
      config: { pageSizeOptions: MRT_PAGE_SIZE_OPTIONS },
    });
  }
}
